// Sign in.
//
// There is no password field, by design. The client enters their email and we
// send a link. Nothing to remember, nothing to reset, and no "forgot
// password" support call — which is the single largest support burden of any
// client portal. They use this twice a year and would forget a password.
//
// The screen has to carry that explanation, because a form with no password
// box looks broken to someone expecting one.

import { useState } from 'react';
import { ApiError, api } from '../api/client.js';

export function SignIn({ linkError }: { linkError?: string | undefined }): JSX.Element {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setStatus('sending');
    try {
      const result = await api.requestSignInLink(email);
      setMessage(result.message);
      setStatus('sent');
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Something went wrong.');
      setStatus('error');
    }
  };

  return (
    <main className="signin">
      <div className="signin__card">
        <div className="brand">
          MyShoebox
          <span className="brand__tag">tax, without the shoebox</span>
        </div>

        {linkError && <output className="notice notice--warn">{linkError}</output>}

        {status === 'sent' ? (
          <>
            <h1>Check your email</h1>
            <p className="muted">{message}</p>
            <p className="muted small">
              The link signs you in and lasts 30 minutes. If it does not arrive, check your spam
              folder.
            </p>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setStatus('idle');
                setMessage('');
              }}
            >
              Use a different email
            </button>
          </>
        ) : (
          <>
            <h1>Your tax questionnaire</h1>
            <p className="muted">
              Enter the email address your accountant has on file and we will send you a sign-in
              link. There is no password to remember.
            </p>

            <form onSubmit={submit}>
              <label className="label" htmlFor="signin-email">
                Email address
              </label>
              <input
                id="signin-email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                // Focused on load: on a phone this is the only thing to do on
                // this screen, so the keyboard should already be up.
                // biome-ignore lint/a11y/noAutofocus: single-purpose screen
                autoFocus
                required
                value={email}
                placeholder="you@example.com"
                onChange={(event) => setEmail(event.target.value)}
              />

              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={status === 'sending' || email.trim() === ''}
              >
                {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </form>

            {status === 'error' && (
              <p className="notice notice--error" role="alert">
                {message}
              </p>
            )}
          </>
        )}

        <p className="signin__alt">
          Work at the practice? <a href="/practice">Staff sign-in</a>
        </p>
      </div>
    </main>
  );
}
